import { zodResolver } from '@hookform/resolvers/zod';
import type React from 'react';
import { useEffect, useMemo } from 'react';
import { type DefaultValues, type FieldValues, type Path, type PathValue, useForm } from 'react-hook-form';
import type { ZodSchema } from 'zod/v3';

type FormControllerOptions<T extends FieldValues> = {
  initialData: T;
  schema: ZodSchema<T>;
  onChange?: (data: T) => void;
};

export function useFormController<T extends FieldValues>({ initialData, schema, onChange }: FormControllerOptions<T>) {
  const form = useForm<T>({
    resolver: zodResolver(schema as never) as never,
    defaultValues: initialData as DefaultValues<T>,
    mode: 'onChange',
  });

  useEffect(() => {
    const subscription = form.watch((value) => {
      onChange?.(value as T);
    });
    return () => subscription.unsubscribe();
  }, [form, onChange]);

  useEffect(() => {
    void form.trigger();
  }, [form]);

  const isValid = form.formState.isValid;

  const errors = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(form.formState.errors).map(([key, errorValue]) => [key, errorValue?.message ? [String(errorValue.message)] : []])
      ) as Record<string, string[]>,
    [form.formState.errors]
  );

  const setField = <K extends Path<T>>(key: K, value: PathValue<T, K>) => {
    form.setValue(key, value, { shouldDirty: true, shouldValidate: true });
  };

  const setFormData = (next: T) => {
    form.reset(next as T);
    onChange?.(next);
  };

  const reset = (next?: T) => {
    const value = next ?? initialData;
    form.reset(value as T);
    onChange?.(value);
  };

  return {
    data: form.watch() as T,
    setData: setFormData,
    setField,
    reset,
    isValid,
    errors,
  };
}

export function createFieldChange<T extends FieldValues>(form: { setField: <K extends Path<T>>(key: K, value: PathValue<T, K>) => void }) {
  return <K extends Path<T>>(key: K) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      form.setField(key, e.target.value as PathValue<T, K>);
    };
}
